import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ClientService } from '../../../../services/forwarding/client.service';
import { Client, CreateClientDto, UpdateClientDto, CLIENT_TYPES } from '../../../../models/forwarding/client.model';

@Component({
  selector: 'app-client-management-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './client-management-modal.html',
  styleUrl: './client-management-modal.css'
})
export class ClientManagementModalComponent implements OnInit, OnDestroy {
  showModal = false;
  showAddModal = false;
  showEditModal = false;
  clients: Client[] = [];
  filteredClients: Client[] = [];
  searchTerm = '';
  private subscriptions = new Subscription();

  // Available currencies and client types
  availableCurrencies = ['USD', 'PHP', 'EUR', 'GBP', 'JPY', 'CNY'];
  readonly clientTypes = CLIENT_TYPES;

  formData: CreateClientDto = {
    name: '',
    clientType: undefined,
    defaultCurrency: 'USD',
    defaultMarkupPercentage: 0,
    isActive: true
  };

  editFormData: UpdateClientDto = {
    name: '',
    clientType: undefined,
    defaultCurrency: 'USD',
    defaultMarkupPercentage: 0,
    isActive: true
  };

  currentEditId: number | null = null;

  constructor(private clientService: ClientService) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.clientService.getClients().subscribe(clients => {
        this.clients = clients;
        this.filteredClients = clients;
        console.log('[ClientManagementModal] Clients updated:', clients.length);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  open(): void {
    this.showModal = true;
    console.log('[ClientManagementModal] Modal opened');
  }

  close(): void {
    this.showModal = false;
    console.log('[ClientManagementModal] Modal closed');
  }

  openAddClientModal(): void {
    this.showAddModal = true;
    this.resetForm();
    console.log('[ClientManagementModal] Add modal opened');
  }

  closeAddClientModal(): void {
    this.showAddModal = false;
    this.resetForm();
  }

  openEditClientModal(client: Client): void {
    this.showEditModal = true;
    this.currentEditId = client.id;
    this.editFormData = {
      name: client.name,
      clientType: client.clientType,
      defaultCurrency: client.defaultCurrency,
      defaultMarkupPercentage: client.defaultMarkupPercentage,
      isActive: client.isActive
    };
    console.log('[ClientManagementModal] Edit modal opened for client:', client.id);
  }

  closeEditClientModal(): void {
    this.showEditModal = false;
    this.currentEditId = null;
    this.editFormData = {
      name: '',
      defaultCurrency: 'USD',
      defaultMarkupPercentage: 0,
      isActive: true
    };
  }

  filterClients(): void {
    const search = this.searchTerm.toLowerCase().trim();
    if (!search) {
      this.filteredClients = this.clients;
      return;
    }

    this.filteredClients = this.clients.filter(c =>
      c.name.toLowerCase().includes(search) ||
      c.defaultCurrency.toLowerCase().includes(search)
    );
  }

  createClient(): void {
    if (!this.formData.name) {
      alert('Please enter a client name');
      return;
    }

    if (this.formData.defaultMarkupPercentage < 0 || this.formData.defaultMarkupPercentage > 100) {
      alert('Markup percentage must be between 0 and 100');
      return;
    }

    this.clientService.create(this.formData).subscribe({
      next: (client) => {
        console.log('[ClientManagementModal] Client created successfully:', client);
        this.closeAddClientModal();
      },
      error: (error) => {
        console.error('[ClientManagementModal] Error creating client:', error);
        alert('Error creating client. Please try again.');
      }
    });
  }

  updateClient(): void {
    if (!this.currentEditId || !this.editFormData.name) {
      alert('Please enter a client name');
      return;
    }

    if (this.editFormData.defaultMarkupPercentage < 0 || this.editFormData.defaultMarkupPercentage > 100) {
      alert('Markup percentage must be between 0 and 100');
      return;
    }

    this.clientService.update(this.currentEditId, this.editFormData).subscribe({
      next: (client) => {
        console.log('[ClientManagementModal] Client updated successfully:', client);
        this.closeEditClientModal();
      },
      error: (error) => {
        console.error('[ClientManagementModal] Error updating client:', error);
        alert('Error updating client. Please try again.');
      }
    });
  }

  deleteClient(id: number): void {
    if (!confirm('Are you sure you want to delete this client?')) {
      return;
    }

    this.clientService.delete(id).subscribe({
      next: () => {
        console.log('[ClientManagementModal] Client deleted successfully');
      },
      error: (error) => {
        console.error('[ClientManagementModal] Error deleting client:', error);
        alert('Error deleting client. Please try again.');
      }
    });
  }

  getClientTypeLabel(type: string | undefined): string {
    if (!type) return '-';
    return this.clientTypes.find(t => t.value === type)?.label ?? type;
  }

  private resetForm(): void {
    this.formData = {
      name: '',
      clientType: undefined,
      defaultCurrency: 'USD',
      defaultMarkupPercentage: 0,
      isActive: true
    };
  }
}
